import { useEffect, useMemo, useState } from 'react';
import { listInvestorOrganizations } from '../../api/investorApi';
import { DataTable } from '../../components/ui/DataTable';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { formatDateTime } from '../../utils/format';
import { humanize, orgTypeLabel } from './investorLabels';

export function InvestorOrganizationsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listInvestorOrganizations({ page, pageSize });
        if (cancelled) return;
        setRows(response?.items || []);
        setTotal(response?.meta?.total ?? 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load organizations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, reloadToken]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Organization',
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">{row.name}</span>
            <span className="block font-mono text-[11px] text-eorig-muted">{row.code}</span>
          </div>
        ),
      },
      { key: 'org_type', header: 'Type', render: (row) => orgTypeLabel(row.org_type) },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusPill value={row.status} label={humanize(row.status)} />,
      },
      { key: 'programs', header: 'Programs', align: 'right' },
      { key: 'packages', header: 'Packages', align: 'right' },
      {
        key: 'created_at',
        header: 'Since',
        render: (row) => <span className="text-eorig-muted">{formatDateTime(row.created_at)}</span>,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((v) => v + 1)} /> : null}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowHref={(row) =>
          row.org_type === 'originator' || row.org_type === 'custodian'
            ? `/investor/packages?organization=${row.id}`
            : undefined
        }
        loading={loading}
        emptyMessage="No organizations."
        minWidth={760}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
